import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import type { RootState, AppDispatch } from '../index';
import { doc, collection, query, onSnapshot, QuerySnapshot, DocumentData, GeoPoint, Timestamp, where } from 'firebase/firestore';
import { db } from '@/lib/firebase';
// Use types from firestore.ts and define serializable versions for state
import type { FirestoreLocation, Sighting as FirestoreSighting } from '@/types/firestore'; // Changed path and renamed Sighting

// Serializable version of FirestoreLocation for Redux state
interface ReduxLocation extends Omit<FirestoreLocation, 'coordinates' | 'lastSightingDate' | 'createdAt' | 'updatedAt'> {
    coordinates: { latitude: number; longitude: number };
    lastSightingDate: number; // Store as milliseconds
    createdAt: number; // Added, store as ms
    updatedAt: number; // Added, store as ms
}

// Serializable version of Sighting for Redux state
interface ReduxSighting extends Omit<FirestoreSighting, 'firstSighted' | 'lastConfirmed'> {
    firstSighted: number; // Store as milliseconds
    lastConfirmed: number; // Store as milliseconds
}

// Type for loading status
type LoadingStatus = 'idle' | 'pending' | 'succeeded' | 'failed';

// Shape for data segments within the slice (using Redux types)
interface DataSegment<T> {
    data: T | null;
    loading: LoadingStatus;
    error: string | null;
}

// Shape for list data segments (using Redux types)
interface ListDataSegment<T> {
    data: T[];
    loading: LoadingStatus;
    error: string | null;
}

// State shape for the active location (using Redux types)
interface ActiveLocationState {
    locationId: string | null;
    details: DataSegment<ReduxLocation>;
    sightings: ListDataSegment<ReduxSighting>;
    // Potential: Add listener unsubscribe functions if managed here
    // unsubscribeDetails?: () => void;
    // unsubscribeSightings?: () => void;
}

const initialDataSegmentState = <T,>(): DataSegment<T> => ({ 
    data: null,
    loading: 'idle',
    error: null,
});

const initialListDataSegmentState = <T,>(): ListDataSegment<T> => ({ 
    data: [],
    loading: 'idle',
    error: null,
});

const initialState: ActiveLocationState = {
    locationId: null,
    details: initialDataSegmentState<ReduxLocation>(), // Use ReduxLocation
    sightings: initialListDataSegmentState<ReduxSighting>(), // Use ReduxSighting
};

// Keep track of active listeners within this slice module
let detailsListenerUnsubscribe: (() => void) | null = null;
let sightingsListenerUnsubscribe: (() => void) | null = null;

// Async Thunk to fetch details and listen for sightings
export const fetchActiveLocationData = createAsyncThunk<
    void, // Return void, updates happen via listeners/reducers
    string, // Argument is locationId
    { dispatch: AppDispatch; rejectValue: string; state: RootState } // Thunk config
>(
    'activeLocation/fetchData',
    async (locationId, { dispatch, getState }) => {
        const currentId = getState().activeLocation.locationId;
        
        // Clear previous listeners if ID changes or is cleared
        if (detailsListenerUnsubscribe && currentId !== locationId) {
            detailsListenerUnsubscribe();
            detailsListenerUnsubscribe = null;
        }
        if (sightingsListenerUnsubscribe && currentId !== locationId) {
            sightingsListenerUnsubscribe();
            sightingsListenerUnsubscribe = null;
        }

        // If locationId is null/empty, clear the state and stop
        if (!locationId) {
            dispatch(activeLocationSlice.actions.clearActiveLocation());
            return;
        }
        
        // Avoid re-setting listeners for the same ID if already active
        if (currentId === locationId && (detailsListenerUnsubscribe || sightingsListenerUnsubscribe)) {
            // Optionally force a refresh if needed, but listeners should keep state updated
            return; 
        }

        // Set loading states and the new ID
        dispatch(activeLocationSlice.actions.setActiveLocationId(locationId));
        dispatch(activeLocationSlice.actions.setDetailsLoading('pending'));
        dispatch(activeLocationSlice.actions.setSightingsLoading('pending'));

        // --- Setup Details Listener ---
        try {
            const detailsDocRef = doc(db, 'locations', locationId);
            detailsListenerUnsubscribe = onSnapshot(detailsDocRef, 
                (docSnap) => {
                    if (docSnap.exists()) {
                        const rawData: DocumentData = docSnap.data();
                        // Transform Firestore data to ReduxLocation
                        const locationData: ReduxLocation = {
                            id: docSnap.id,
                            name: rawData.name,
                            address: rawData.address,
                            url: rawData.url,
                            locationType: rawData.locationType,
                            googlePlaceId: rawData.googlePlaceId,
                            averageRating: rawData.averageRating,
                            ratingCount: rawData.ratingCount,
                            drinkCount: rawData.drinkCount,
                            // Convert GeoPoint
                            coordinates: rawData.coordinates instanceof GeoPoint
                                ? { latitude: rawData.coordinates.latitude, longitude: rawData.coordinates.longitude }
                                : { latitude: 0, longitude: 0 }, // Provide a default/fallback
                            // Convert Timestamps
                            lastSightingDate: rawData.lastSightingDate instanceof Timestamp
                                ? rawData.lastSightingDate.toMillis()
                                : 0, // Provide a default/fallback
                            createdAt: rawData.createdAt instanceof Timestamp // Added
                                ? rawData.createdAt.toMillis()
                                : 0,
                            updatedAt: rawData.updatedAt instanceof Timestamp // Added
                                ? rawData.updatedAt.toMillis()
                                : 0,
                        };

                        dispatch(activeLocationSlice.actions.setDetailsSuccess(locationData)); // Dispatch ReduxLocation
                    } else {
                        console.warn(`[ActiveLocation] Location document ${locationId} does not exist.`);
                        dispatch(activeLocationSlice.actions.setDetailsError('Location not found'));
                    }
                }, 
                (error) => {
                    console.error(`[ActiveLocation] Error listening to details for ${locationId}:`, error);
                    dispatch(activeLocationSlice.actions.setDetailsError(error.message));
                    detailsListenerUnsubscribe = null; // Clear on error
                }
            );
        } catch (error: any) {
            console.error(`[ActiveLocation] Failed to set up details listener for ${locationId}:`, error);
            dispatch(activeLocationSlice.actions.setDetailsError(error.message));
            detailsListenerUnsubscribe = null;
            // Consider rejecting if details setup fails critically?
        }
        
        // --- Setup Sightings Listener ---
        try {
            // Query the root 'sightings' collection, filtering by locationId
            const sightingsQuery = query(
                collection(db, 'sightings'), 
                where('locationId', '==', locationId)
                // Temporarily removing orderBy to debug
                // orderBy('lastConfirmed', 'desc') 
            );
            
            sightingsListenerUnsubscribe = onSnapshot(sightingsQuery, 
                (snapshot: QuerySnapshot<DocumentData>) => {
                    
                    const sightingsData = snapshot.docs.map(doc => {
                        const rawData: DocumentData = doc.data();
                        // Transform FirestoreSighting to ReduxSighting
                        const transformedSighting: ReduxSighting = {
                           id: doc.id,
                           locationId: rawData.locationId,
                           locationName: rawData.locationName,
                           drinkId: rawData.drinkId,
                           drinkName: rawData.drinkName,
                           brandId: rawData.brandId,
                           confirmationCount: rawData.confirmationCount,
                           // Convert Timestamps
                           firstSighted: rawData.firstSighted instanceof Timestamp
                               ? rawData.firstSighted.toMillis()
                               : 0, // Default/fallback
                           lastConfirmed: rawData.lastConfirmed instanceof Timestamp
                               ? rawData.lastConfirmed.toMillis()
                               : 0, // Default/fallback
                        };
                        return transformedSighting;
                    });
                    
                    dispatch(activeLocationSlice.actions.setSightingsSuccess(sightingsData)); // Dispatch ReduxSighting[]
                }, 
                (error) => {
                    console.error(`[ActiveLocation] Error listening to sightings for ${locationId}:`, error);
                    
                    // Check for index errors specifically
                    if (error.message && (
                        error.message.includes("index does not exist") || 
                        error.message.includes("requires an index") ||
                        error.message.includes("found no matching index")
                    )) {
                        console.error("FIRESTORE INDEX ERROR: This query requires a composite index that doesn't exist.");
                        console.error("Create the required index in the Firebase console using the link in the error message.");
                    }
                    
                    dispatch(activeLocationSlice.actions.setSightingsError(error.message));
                    sightingsListenerUnsubscribe = null; // Clear on error
                }
            );
        } catch (error: any) {
            console.error(`[ActiveLocation] Failed to set up sightings listener for ${locationId}:`, error);
            dispatch(activeLocationSlice.actions.setSightingsError(error.message));
            sightingsListenerUnsubscribe = null;
             // Consider rejecting if sightings setup fails critically?
        }
    }
);

// Slice Definition
const activeLocationSlice = createSlice({
    name: 'activeLocation',
    initialState,
    reducers: {
        setActiveLocationId: (state, action: PayloadAction<string | null>) => {
            // Clear previous state only if ID actually changes
            if (state.locationId !== action.payload) {
                state.locationId = action.payload;
                state.details = initialDataSegmentState<ReduxLocation>(); // Use ReduxLocation
                state.sightings = initialListDataSegmentState<ReduxSighting>(); // Use ReduxSighting
                // Listener cleanup happens in the thunk
            }
        },
        clearActiveLocation: (state) => {
            state.locationId = null;
            state.details = initialDataSegmentState<ReduxLocation>(); // Use ReduxLocation
            state.sightings = initialListDataSegmentState<ReduxSighting>(); // Use ReduxSighting
            // Listener cleanup should be triggered by dispatching fetchActiveLocationData(null)
        },
        // Details Reducers
        setDetailsLoading: (state, action: PayloadAction<LoadingStatus>) => {
            state.details.loading = action.payload;
            if (action.payload === 'pending') state.details.error = null;
        },
        setDetailsSuccess: (state, action: PayloadAction<ReduxLocation>) => { // Expect ReduxLocation
            state.details.data = action.payload; // Assign ReduxLocation
            state.details.loading = 'succeeded';
            state.details.error = null;
        },
        setDetailsError: (state, action: PayloadAction<string>) => {
            state.details.loading = 'failed';
            state.details.error = action.payload;
        },
        // Sightings Reducers
        setSightingsLoading: (state, action: PayloadAction<LoadingStatus>) => {
            state.sightings.loading = action.payload;
             if (action.payload === 'pending') state.sightings.error = null;
        },
        setSightingsSuccess: (state, action: PayloadAction<ReduxSighting[]>) => { // Expect ReduxSighting[]
            state.sightings.data = action.payload; // Assign ReduxSighting[]
            state.sightings.loading = 'succeeded';
            state.sightings.error = null;
        },
        setSightingsError: (state, action: PayloadAction<string>) => {
            state.sightings.loading = 'failed';
            state.sightings.error = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchActiveLocationData.pending, () => {
                // Loading state is handled within the thunk/reducers
            })
            .addCase(fetchActiveLocationData.rejected, (_, action) => {
                console.error("[ActiveLocation] Fetch rejected:", action.payload);
                // Reset state on rejection, maybe set a general error?
                // State should ideally be handled by specific error reducers called within the thunk.
            })
            .addCase(fetchActiveLocationData.fulfilled, () => {
                 // Listeners are now active (or cleared if arg was null)
            });
    },
});

// Export actions and reducer
export const { 
    setActiveLocationId, 
    clearActiveLocation, 
    setDetailsLoading, 
    setDetailsSuccess, 
    setDetailsError, 
    setSightingsLoading, 
    setSightingsSuccess, 
    setSightingsError 
} = activeLocationSlice.actions;
export default activeLocationSlice.reducer;

// --- Selectors ---
export const selectActiveLocationId = (state: RootState) => state.activeLocation.locationId;
// Selectors now return the Redux-specific types
export const selectActiveLocationDetails = (state: RootState): ReduxLocation | null => state.activeLocation.details.data;
export const selectActiveLocationDetailsLoading = (state: RootState) => state.activeLocation.details.loading;
export const selectActiveLocationDetailsError = (state: RootState) => state.activeLocation.details.error;

export const selectActiveLocationSightings = (state: RootState): ReduxSighting[] => state.activeLocation.sightings.data;
export const selectActiveLocationSightingsLoading = (state: RootState) => state.activeLocation.sightings.loading;
export const selectActiveLocationSightingsError = (state: RootState) => state.activeLocation.sightings.error;

// Combined loading state for the active location view
export const selectActiveLocationIsLoading = (state: RootState) => 
    state.activeLocation.details.loading === 'pending' || 
    state.activeLocation.sightings.loading === 'pending';

// Export the types needed externally
export type { ReduxLocation, ReduxSighting }; 